import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, ChevronDown, ChevronUp, Info, Wand2 } from "lucide-react";
import { BotonCostoCorrecto, CostoRevisadoLista } from "@/components/CostoRevisado";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const MOTIVO = {
  costo_supera_precio: "Costo del lote ≥ precio de venta",
  margen_menor_10: "Margen bruto < 10%",
  desviado_vs_ficha: "Costo del lote difiere > 30% del costo de la ficha",
};

/** Finanzas → Detector de lotes con costo inflado (solo lectura). */
export default function AuditoriaLotesPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.get("/reportes/auditoria-lotes")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo revisar los lotes"))
      .finally(() => setLoading(false));
  }, [open, data]);

  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="auditoria-lotes-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="auditoria-lotes-toggle">
        <div className="flex items-center gap-2">
          <Boxes className="w-4 h-4 text-rose-600" />
          <div>
            <div className="text-sm font-semibold text-foreground">Lotes con costo inflado · ¿los lotes cuestan más que el precio de venta?</div>
            <div className="text-xs text-muted-foreground">Compara el costo unitario de cada lote con el costo de la ficha y el precio mínimo. Detecta paquetes maestros que quedaron al costo del paquete completo.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-sm text-muted-foreground">Revisando lotes…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Kpi label="Productos afectados" value={`${data.productos_afectados} / ${data.productos_revisados}`} sub={`${data.lotes_sospechosos} de ${data.lotes_revisados} lotes`} warn={data.productos_afectados > 0} testid="lotes-productos-afectados" />
                <Kpi label="Inventario inflado" value={formatCOP(data.inventario_inflado_total)} sub="Valor actual − valor a costo de ficha" warn={data.inventario_inflado_total > 0} testid="lotes-inventario-inflado" />
                <Kpi label="COGS histórico inflado" value={formatCOP(data.cogs_inflado_total)} sub={`${data.despachos_afectados} despacho(s) consumieron estos lotes`} warn={data.cogs_inflado_total > 0} testid="lotes-cogs-inflado" />
                <Kpi label="Utilidad que recuperarías" value={formatCOP(data.cogs_inflado_total)} sub="Si los despachos se re-valoran al costo de ficha" accent testid="lotes-utilidad-recuperable" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2 flex-1 min-w-[260px]">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    Cuando corriges la cantidad de un lote o el costo en la ficha <b>a mano</b>, el costo unitario del lote NO cambia. Cada despacho sigue consumiendo el lote al costo viejo (inflado), por eso la utilidad baja aunque vendas más. Este panel solo <b>muestra</b>: no modifica nada.
                  </div>
                </div>
                <Link to="/configuracion?tab=sistema" className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90" data-testid="lotes-ir-desglose-asistido">
                  <Wand2 className="w-3.5 h-3.5" /> Ir a desglose asistido
                </Link>
              </div>
              {data.productos_afectados === 0 && <div className="text-sm text-emerald-700" data-testid="lotes-sin-problemas">Ningún lote con costo sospechoso.</div>}
              <CostoRevisadoLista total={data.revisados} onCambio={() => setData(null)} />
              {data.productos_afectados > 0 && (
                <div className="max-h-96 overflow-auto rounded-md border border-border">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Producto</th><th className="text-right p-2">Costo ficha</th><th className="text-right p-2">Precio mín.</th><th className="text-right p-2">Lotes</th><th className="text-right p-2">Stock en lotes</th><th className="text-right p-2">Inventario inflado</th><th className="text-right p-2">COGS inflado</th><th className="text-left p-2">Motivo</th><th className="p-2"></th></tr></thead>
                    <tbody>
                      {data.productos.map((r, i) => (
                        <ProductoRow key={r.producto_id} r={r} i={i} expanded={expanded === r.producto_id} onToggle={() => setExpanded(expanded === r.producto_id ? null : r.producto_id)} onRevisado={() => setData(null)} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProductoRow({ r, i, expanded, onToggle, onRevisado }) {
  return (
    <>
      <tr className="border-t border-border/60 tabular-nums cursor-pointer hover:bg-muted/30" onClick={onToggle} data-testid={`lotes-fila-${i}`}>
        <td className="p-2 max-w-[220px]"><div className="truncate font-medium">{r.producto}</div>{r.codigo && <div className="text-muted-foreground">{r.codigo}</div>}</td>
        <td className="p-2 text-right">{formatCOP(r.costo_ficha)}</td>
        <td className="p-2 text-right">{formatCOP(r.precio)}</td>
        <td className="p-2 text-right">{r.lotes.length}</td>
        <td className="p-2 text-right">{r.stock_en_lotes}</td>
        <td className={`p-2 text-right ${r.inventario_inflado > 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{r.inventario_inflado ? formatCOP(r.inventario_inflado) : "—"}</td>
        <td className={`p-2 text-right ${r.cogs_inflado > 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>{r.cogs_inflado ? formatCOP(r.cogs_inflado) : "—"}</td>
        <td className="p-2 text-muted-foreground">{r.motivos.map((m) => MOTIVO[m] || m).join(" · ")}</td>
        <td className="p-2"><BotonCostoCorrecto productoId={r.producto_id} nombre={r.producto} onDone={onRevisado} testid={`lotes-costo-ok-${i}`} /></td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20" data-testid={`lotes-detalle-${i}`}>
          <td colSpan={9} className="p-2">
            <table className="w-full text-[10px] tabular-nums">
              <thead><tr className="text-muted-foreground"><th className="text-left p-1">Contenedor</th><th className="text-left p-1">Recibido</th><th className="text-right p-1">Costo unit. lote</th><th className="text-right p-1">Cant. recibida</th><th className="text-right p-1">Disponible</th><th className="text-right p-1">× costo ficha</th></tr></thead>
              <tbody>
                {r.lotes.map((lo) => (
                  <tr key={lo.lote_id} className="border-t border-border/40">
                    <td className="p-1">{lo.contenedor_id || "—"}</td>
                    <td className="p-1">{lo.fecha?.slice(0, 10) || "—"}</td>
                    <td className="p-1 text-right text-rose-600 font-semibold">{formatCOP(lo.costo_unitario)}</td>
                    <td className="p-1 text-right">{lo.recibida}</td>
                    <td className="p-1 text-right">{lo.disponible}</td>
                    <td className="p-1 text-right">{r.costo_ficha > 0 ? `${(lo.costo_unitario / r.costo_ficha).toFixed(1)}×` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function Kpi({ label, value, sub, warn, accent, testid }) {
  return (
    <div className={`rounded-lg border p-2.5 ${warn ? "border-rose-300 bg-rose-50/60 dark:bg-rose-900/15" : accent ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
